import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from './index';
import { LanguageContext, detectLanguage, storeLanguage, type Language } from './language';

/**
 * Holds the chosen language and keeps everything that depends on it in step: the i18next instance,
 * the stored preference, and the document's own `lang` attribute — which is what a screen reader
 * picks its pronunciation from, and the one part of the page React does not own.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  // The same detection the i18next singleton already ran, so the two start out agreeing.
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    storeLanguage(next);

    // Resolves synchronously — the resources are bundled, so nothing is fetched here.
    void i18n.changeLanguage(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LanguageContext.Provider>
  );
}
